import { useRef, lazy, Suspense, useCallback } from 'react';
import { string, arrayOf, func, shape, number, bool } from 'prop-types';
import * as styles from './styles/styles.module.scss';
import MagGlassIcon from '../../svg/search/MagGlassIcon';
import SearchInput from '../SearchInput';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import ArrowRec from '@alltrails/shared/icons/ArrowRec';
import { defineMessage, useIntl } from '@alltrails/shared/react-intl';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';

const SearchResultsDropdown = lazy(() => import('../SearchResultsDropdown'));

const SearchBar = ({
  query,
  loading,
  updateQuery,
  results,
  filters,
  activeFilterIndex,
  updateActiveTabFilter,
  toggleEngaged,
  engaged,
  activeIndexResult,
  updateActiveIndexResult,
  handleSearchResultClick,
  handleSearchBoxFocus,
  mobileSearchBar,
  isMobileWidth
}) => {
  const languageRegionCode = useLanguageRegionCode();
  const inputRef = useRef();
  const intl = useIntl();

  const handleSubmit = e => {
    e.preventDefault();

    const submittedResult = results[activeIndexResult];
    if (submittedResult) {
      handleSearchResultClick(submittedResult, activeIndexResult);
    }
  };

  const handleFocus = () => {
    handleSearchBoxFocus();
    toggleEngaged(true);
  };

  const handleBackgroundClick = useCallback(() => {
    toggleEngaged(false);
  }, []);

  const placeholderTextMessage = defineMessage({
    defaultMessage: 'Search by city, park, or trail name'
  });

  const placeholderText = intl.formatMessage(placeholderTextMessage);

  return (
    <>
      <div id="searchBar" className={styles.container}>
        <form className={styles.form} onSubmit={handleSubmit} aria-label={placeholderText}>
          <MagGlassIcon />
          <SearchInput
            inputRef={inputRef}
            query={query}
            loading={loading}
            results={results}
            handleSearchResultClick={handleSearchResultClick}
            handleExit={() => toggleEngaged(false)}
            onFocus={handleFocus}
            updateQuery={updateQuery}
            className={styles.searchInput}
            placeholderText={placeholderText}
            readOnly={__AT_DATA__.mobileBrowser} // using readOnly here for focus management for the MobileSearchBar component on IOS
          />
          {!isMobileWidth && (
            <IconButton
              icon={{ Component: ArrowRec, orientation: 'right' }}
              size="md"
              testId="algoliaSearchButton"
              title="icon"
              type="submit"
              variant="primary"
            />
          )}
        </form>
        {engaged && !mobileSearchBar && (
          <Suspense fallback={null}>
            <div className={styles.dropdownContainer}>
              <div className={styles.offsetSpacer} />
              <SearchResultsDropdown
                results={results}
                filters={filters}
                activeFilterIndex={activeFilterIndex}
                updateActiveTabFilter={updateActiveTabFilter}
                query={query}
                languageRegionCode={languageRegionCode}
                activeIndexResult={activeIndexResult}
                handleSearchResultClick={handleSearchResultClick}
                updateActiveIndexResult={updateActiveIndexResult}
              />
            </div>
          </Suspense>
        )}
      </div>
      {engaged && <div className={styles.searchBackground} onClick={handleBackgroundClick} />}
    </>
  );
};

SearchBar.propTypes = {
  query: string,
  updateQuery: func.isRequired,
  results: arrayOf(
    shape({
      name: string,
      location: string,
      objectID: string,
      url: string,
      type: string
    })
  ),
  filters: arrayOf(shape({ key: string, label: string })),
  activeFilterIndex: number,
  updateActiveTabFilter: func,
  toggleEngaged: func,
  engaged: bool,
  activeIndexResult: number,
  updateActiveIndexResult: func,
  handleSearchResultClick: func,
  handleSearchBoxFocus: func,
  mobileSearchBar: bool,
  loading: bool,
  isMobileWidth: bool
};

SearchBar.defaultProps = {
  query: '',
  results: [],
  filters: null,
  activeFilterIndex: null,
  updateActiveTabFilter: () => {},
  toggleEngaged: () => {},
  engaged: false,
  activeIndexResult: 0,
  updateActiveIndexResult: () => {},
  handleSearchResultClick: () => {},
  handleSearchBoxFocus: () => {},
  mobileSearchBar: false,
  loading: false,
  isMobileWidth: false
};

export default SearchBar;
